import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, IconButton, Popover, Text, Tooltip } from '@fly/core';
import { IThread } from '@fly/shared';
import { FlyTextLoading } from '@fly/shared-ui';
import {
  openDialog,
  useAppDispatch,
  useListThreadsInfiniteQuery,
} from '@fly/store';
import { useVirtualizer } from '@tanstack/react-virtual';

import {
  ClearChatConfirmation,
  SKIP_CLEAR_CHAT_DIALOG_KEY,
} from '../FlyChatHeader/ClearChatConfirmation';
import {
  ChatHistoryRow,
  ChatHistoryRowSkeleton,
  SKELETON_ROWS,
} from './ChatHistoryRow';
import { groupThreadsByDate } from './groupThreadsByDate';

type FlatItem =
  | { type: 'header'; labelKey: string; defaultLabel: string }
  | { type: 'thread'; thread: IThread };

const ESTIMATED_HEADER_HEIGHT = 18;
const ESTIMATED_ROW_HEIGHT = 52;
const VIRTUALIZER_OVERSCAN = 5;
const LOAD_MORE_THRESHOLD_PX = 100;

interface FlyChatHistoryPopoverProps {
  onSelectThread: (threadId: string) => void;
  onNewChat: () => void;
  isChatEmpty?: boolean;
  currentThreadId?: string;
  onDeleteThread: (threadId: string) => void;
}

export const FlyChatHistoryPopover: React.FC<FlyChatHistoryPopoverProps> = ({
  onSelectThread,
  onNewChat,
  isChatEmpty = false,
  currentThreadId,
  onDeleteThread,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const scrollElRef = useRef<HTMLDivElement | null>(null);
  const [, scrollElGeneration] = useState(0);

  const scrollRefCallback = useCallback((node: HTMLDivElement | null) => {
    scrollElRef.current = node;
    if (node) scrollElGeneration((c) => c + 1);
  }, []);

  const { data, isLoading, fetchNextPage, isFetchingNextPage, hasNextPage } =
    useListThreadsInfiniteQuery();

  const threads = useMemo(() => data?.pages.flat() ?? [], [data?.pages]);

  const groups = useMemo(() => groupThreadsByDate(threads), [threads]);

  const flatItems = useMemo<FlatItem[]>(() => {
    const items: FlatItem[] = [];
    for (const group of groups) {
      items.push({
        type: 'header',
        labelKey: group.labelKey,
        defaultLabel: group.defaultLabel,
      });
      for (const thread of group.threads) {
        items.push({ type: 'thread', thread });
      }
    }
    return items;
  }, [groups]);

  const virtualizer = useVirtualizer({
    count: flatItems.length,
    getScrollElement: () => scrollElRef.current,
    estimateSize: (index) =>
      flatItems[index].type === 'header'
        ? ESTIMATED_HEADER_HEIGHT
        : ESTIMATED_ROW_HEIGHT,
    overscan: VIRTUALIZER_OVERSCAN,
  });

  const handleScroll = useCallback(() => {
    const el = scrollElRef.current;
    if (!hasNextPage || isLoading || isFetchingNextPage || !el) return;
    const { scrollHeight, scrollTop, clientHeight } = el;
    if (scrollHeight - scrollTop - clientHeight < LOAD_MORE_THRESHOLD_PX) {
      fetchNextPage();
    }
  }, [hasNextPage, isLoading, isFetchingNextPage, fetchNextPage]);

  const handleSelect = useCallback(
    (threadId: string) => {
      onSelectThread(threadId);
      setOpen(false);
    },
    [onSelectThread]
  );

  const handleDeleteThread = useCallback(
    (threadId: string) => {
      let skipDialog = false;
      try {
        skipDialog =
          localStorage.getItem(SKIP_CLEAR_CHAT_DIALOG_KEY) === 'true';
      } catch {
        /* Safari private browsing */
      }
      if (skipDialog) {
        onDeleteThread(threadId);

        return;
      }
      dispatch(
        openDialog({
          width: '3',
          title: t('app.dashboard.chat.clear_confirmation.title', {
            defaultValue: 'Are you sure you want to delete this chat?',
          }),
          content: (
            <ClearChatConfirmation onConfirm={() => onDeleteThread(threadId)} />
          ),
        })
      );
    },
    [dispatch, onDeleteThread, t]
  );

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Tooltip
        content={t('app.dashboard.chat.history_tooltip', {
          defaultValue: 'Chat History',
        })}
        side='bottom'
      >
        <Popover.Trigger>
          <IconButton
            data-testid='chat-history-btn'
            aria-label={t('app.dashboard.chat.history_tooltip', {
              defaultValue: 'Chat History',
            })}
            size='1'
            color='neutral'
            variant='ghost'
            icon='history'
          />
        </Popover.Trigger>
      </Tooltip>
      <Popover.Content
        position='bottom'
        align='end'
        color='neutral'
        variant='classic'
        arrow={false}
        data-testid='chat-history-panel'
        className='min-w-[260px]'
        header={
          <div className='flex items-center justify-between px-fly-2'>
            <Text size='3' weight='bold' highContrast>
              {t('app.dashboard.chat.history.title', {
                defaultValue: 'Chat History',
              })}
            </Text>
            <IconButton
              size='1'
              color='neutral'
              variant='ghost'
              icon='cross'
              onClick={() => setOpen(false)}
            />
          </div>
        }
        body={
          <div className='pl-fly-2'>
            {isLoading ? (
              <FlyTextLoading
                textSize='3'
                text={t('app.common.loading', { defaultValue: 'Loading' })}
              />
            ) : threads.length === 0 ? (
              <Text size='3' color='gray' highContrast>
                {t('app.dashboard.chat.history.empty', {
                  defaultValue: 'No conversations yet',
                })}
              </Text>
            ) : (
              <div
                ref={scrollRefCallback}
                onScroll={handleScroll}
                className='max-h-[300px] [scrollbar-width:thin] [scrollbar-color:var(--gray-a8)_transparent] overflow-x-hidden overflow-y-auto py-fly-2 pr-fly-2'
              >
                <div
                  className='relative w-full'
                  style={{ height: `${virtualizer.getTotalSize()}px` }}
                >
                  {virtualizer.getVirtualItems().map((virtualItem) => {
                    const item = flatItems[virtualItem.index];
                    return (
                      <div
                        key={virtualItem.key}
                        ref={virtualizer.measureElement}
                        data-index={virtualItem.index}
                        className='top-0 left-0 absolute w-full'
                        style={{
                          transform: `translateY(${virtualItem.start}px)`,
                        }}
                      >
                        {item.type === 'header' ? (
                          <Text
                            size='1'
                            color='gray'
                            className={`mb-fly-1 block${virtualItem.index > 0 ? ' mt-fly-2' : ''}`}
                          >
                            {t(item.labelKey, {
                              defaultValue: item.defaultLabel,
                            })}
                          </Text>
                        ) : (
                          <ChatHistoryRow
                            thread={item.thread}
                            isActive={item.thread.thread_id === currentThreadId}
                            onSelect={handleSelect}
                            onDelete={handleDeleteThread}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
                {isFetchingNextPage && (
                  <div className='pt-fly-2'>
                    {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                      <ChatHistoryRowSkeleton key={`skeleton-${i}`} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        }
        footer={
          <div className='px-fly-2'>
            <Button
              className='w-full justify-start'
              variant='ghost'
              size='2'
              iconStart='plus'
              color='accent'
              disabled={isChatEmpty}
              highContrast
              onClick={() => {
                onNewChat();
                setOpen(false);
              }}
            >
              {t('app.dashboard.chat.start_new_chat_tooltip', {
                defaultValue: 'Start New Chat',
              })}
            </Button>
          </div>
        }
        insetContent
      />
    </Popover.Root>
  );
};
